import { Component, signal, computed, ViewChild, AfterViewInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule, MatSidenav } from '@angular/material/sidenav';
import { MatButtonModule } from '@angular/material/button';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AuthService } from '../../../features/auth/auth.service';

interface SidebarItem {
  label: string;
  icon?: string;
  route?: string;
  children?: SidebarItem[];
  allowedRoles?: string[];
}

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule, MatSidenavModule, MatButtonModule],
  templateUrl: './sidebar.html',
  styleUrls: ['./sidebar.scss'],
})
export class Sidebar implements AfterViewInit {
  private readonly authService = inject(AuthService);
  @ViewChild('drawer') sidenav!: MatSidenav; // usa el template reference explícito
  isMobile = signal(false);
  activeGroups = signal<Record<string, boolean>>({});
  userFullName = signal<string | null>(localStorage.getItem('user_full_name'));
  roles = this.authService.roles;

  constructor(
    private router: Router,
    breakpoint: BreakpointObserver,
  ) {
    breakpoint.observe(['(max-width: 959px)']).subscribe((result) => {
      const mobile = result.matches;
      this.isMobile.set(mobile);

      if (!this.sidenav) return;

      if (mobile) {
        this.sidenav.close();
      } else {
        this.sidenav.open();
      }
    });
    window.addEventListener('storage', () => {
      this.userFullName.set(localStorage.getItem('user_full_name'));
    });
  }

  private readonly menu: SidebarItem[] = [
    { label: 'Inicio', icon: 'home', route: '/', allowedRoles: [] },
    { label: 'Usuarios', icon: 'group', route: '/usuarios', allowedRoles: ['admin', 'superadmin'] },
    { label: 'Grupo Familiar', icon: 'group', route: '/familia', allowedRoles: ['admin', 'superadmin', 'administrative'] },
    { label: 'Vínculos Familiares', icon: 'link', route: '/vinculos', allowedRoles: ['admin', 'superadmin', 'backoffice', 'profesional', 'paciente'] },
    {
      label: 'Validaciones',
      icon: 'verified_user',
      allowedRoles: ['admin', 'superadmin', 'administrative'],
      children: [{ label: 'Validaciones de identidad', route: '/validaciones/identidad', allowedRoles: ['admin', 'superadmin', 'administrative'] }],
    },
    { label: 'Administrativos', icon: 'person', route: '/administradores', allowedRoles: ['admin', 'superadmin'] },
    { label: 'Pacientes', icon: 'medical_services', route: '/pacientes', allowedRoles: ['admin', 'superadmin', 'backoffice', 'profesional', 'paciente'] },
    { label: 'Profesionales', icon: 'medical_services', route: '/profesionales', allowedRoles: ['admin', 'superadmin', 'backoffice', 'profesional', 'paciente'] },
    {
      label: 'Configuración',
      icon: 'settings',
      allowedRoles: ['admin', 'superadmin', 'backoffice', 'profesional', 'paciente'],
      children: [
        { label: 'Especialidades', route: '/config/especialidades', allowedRoles: ['admin', 'superadmin', 'backoffice', 'profesional', 'paciente'] },
        { label: 'Obras sociales', route: '/config/obras-sociales', allowedRoles: ['admin', 'superadmin', 'backoffice', 'profesional', 'paciente'] },
      ],
    },
    {
      label: 'Mi Perfil',
      icon: 'business',
      children: [
        { label: 'Perfil', route: '/perfil/datos-personales' },
        { label: 'Cambiar clave', route: '/perfil/restablecer-clave' },
      ],
    },
    { label: 'Cerrar sesión', icon: 'logout' },
  ];

  filteredMenu = computed(() => this.menu.filter((item) => this.canSeeItem(item)));

  toggleGroup(label: string) {
    this.activeGroups.update((v) => ({ ...v, [label]: !v[label] }));
  }

  isOpen(label: string) {
    return !!this.activeGroups()[label];
  }

  canSeeItem(item: SidebarItem): boolean {
    const userRoles = this.roles();

    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return !item.children || item.children.length === 0 || item.children.some((child) => this.canSeeItem(child));
    }

    const hasAccess = item.allowedRoles.some((role) => userRoles.includes(role));
    if (!hasAccess) {
      return false;
    }

    if (!item.children || item.children.length === 0) {
      return true;
    }

    return item.children.some((child) => this.canSeeItem(child));
  }

  visibleChildren(item: SidebarItem): SidebarItem[] {
    return (item.children ?? []).filter((child) => this.canSeeItem(child));
  }

  toggleSidebar() {
    if (!this.sidenav) return;
    this.sidenav.toggle();
  }

  ngAfterViewInit() {
    if (this.isMobile()) {
      this.sidenav.close();
    } else {
      this.sidenav.open();
    }
  }

  navigate(item: SidebarItem) {
    if (item.label === 'Cerrar sesión') {
      this.logout();
      return;
    }

    if (item.route) {
      this.router.navigate([item.route]);
      if (this.isMobile()) this.sidenav.close();
    }
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/login']);
  }

  roleLabel = computed(() => {
    const role = this.roles()[0];
    return role ? role.toUpperCase() : null;
  });
}
